import { getAuth } from 'firebase/auth';
import { realtimeSocketHub } from './realtimeSocketHub';
import { isMessagingAccessBlocked } from './messagingAccess';
import hybridApi, {
  messagingGetWithAuth,
  messagingPatchWithAuth,
  messagingPostWithAuth,
} from './hybridApiClient';
import { hybridApiBaseUrl, resolveMessagingApiBaseUrl } from './hybridFlags';
import { GROUP_MESSAGE_TO_USER_ID, Message, MessageConversationMeta } from '../types/typesMessages';
import { notifyIncomingMessage } from './messageNotificationService';

type Unsubscribe = () => void;
type SendMessageInput = Omit<Message, 'id' | 'createdAt' | 'isRead'> & {
  attachmentFile?: File | null;
  conversationId?: string;
  toUserIds?: string[];
  groupTitle?: string;
  toUserNames?: Record<string, string>;
  memberRoles?: Record<string, string>;
  memberPhotos?: Record<string, string>;
};
type MessageCallback = (messages: Message[]) => void;
type UnreadCallback = (count: number) => void;
type UnreadCountEvent = { userId?: string; count?: number };
type MessageEventPayload = { message?: Message };
type MessageRecipient = { userId: string; userName: string };

type MessagesApiCapabilities = {
  groups?: boolean;
  groupToUserId?: string;
};

type MessagesPostResponse = {
  id?: string;
  conversationId?: string;
  broadcast?: boolean;
  messages?: Array<{ id?: string; conversationId?: string }>;
  memberIds?: string[];
  conversationType?: string;
};

class MessagingService {
  private serverSupportsGroupApi: boolean | null = null;
  private unsubscribeCallbacks: Map<string, Unsubscribe> = new Map();
  private messageSubscribers: Map<string, Set<MessageCallback>> = new Map();
  private unreadSubscribers: Map<string, Set<UnreadCallback>> = new Map();
  private pendingMessageRefreshTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  /** מניעת שתי בקשות GET לרשימה באותה עת (דיאלוג + התקשרות subscribe — אותם נתונים) */
  private pendingUserMessages: Map<string, Promise<Message[]>> = new Map();
  /** dedup לבקשת unread-count מקבילה (subscribe + socket connect + mark-read רצים יחד) */
  private pendingUnreadCount: Map<string, Promise<number>> = new Map();
  private messagesCacheByUserId: Map<string, Message[]> = new Map();
  private unreadCountCacheByUserId: Map<string, number> = new Map();
  /** ה-listeners של ההודעות מוצמדים ל-socket המשותף (realtimeSocketHub) פעם אחת. */
  private socketListenersRegistered = false;
  private detachSocketConsumer: (() => void) | null = null;
  private detachConnectListener: (() => void) | null = null;
  private detachWakeListener: (() => void) | null = null;

  private messagingAllowed(): boolean {
    return !isMessagingAccessBlocked();
  }

  async probeGroupApiSupport(userId: string, force = false): Promise<boolean> {
    if (!this.messagingAllowed()) return false;
    const uid = String(userId || '').trim();
    if (!uid) return false;
    if (!force && this.serverSupportsGroupApi !== null) return this.serverSupportsGroupApi;
    if (!hybridApiBaseUrl) {
      this.serverSupportsGroupApi = false;
      return false;
    }
    try {
      const cap = await messagingGetWithAuth<MessagesApiCapabilities>('/api/messages/capabilities', {
        timeout: 8000,
      });
      if (cap.data?.groups === true) {
        this.serverSupportsGroupApi = true;
        return true;
      }
      await messagingGetWithAuth('/api/messages/conversations', {
        params: { userId: uid },
        timeout: 8000,
      });
      this.serverSupportsGroupApi = true;
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (status === 404) {
        this.serverSupportsGroupApi = false;
      } else if (status === 401 || status === 403) {
        // הנתיב קיים — ננסה שליחת קבוצה בכל זאת (עם טוקן ב-POST)
        this.serverSupportsGroupApi = true;
      }
    }
    return this.serverSupportsGroupApi ?? false;
  }

  /** שליחה נפרדת לכל נמען — תמיד לולאה, עם שם נמען נכון */
  async sendToRecipients(
    message: SendMessageInput,
    recipients: MessageRecipient[],
  ): Promise<string> {
    const list = recipients
      .map((r) => ({
        userId: String(r.userId || '').trim(),
        userName: String(r.userName || '').trim() || 'משתמש',
      }))
      .filter((r) => r.userId && r.userId !== String(message.fromUserId || '').trim());
    if (list.length === 0) throw new Error('No recipients');
    let lastId = '';
    for (let i = 0; i < list.length; i++) {
      const r = list[i];
      const sent = await this.sendMessageOnce({
        ...message,
        toUserId: r.userId,
        toUserName: r.userName,
        toUserIds: undefined,
        toUserNames: undefined,
        memberRoles: undefined,
        memberPhotos: undefined,
        groupTitle: undefined,
        conversationId: undefined,
        attachmentFile: message.attachmentFile,
      });
      lastId = sent.id;
    }
    return lastId;
  }

  async sendGroupFirstMessage(
    message: SendMessageInput,
    recipients: MessageRecipient[],
    groupTitle: string,
  ): Promise<{ id: string; conversationId?: string }> {
    const title = String(groupTitle || '').trim() || 'קבוצה';
    const list = recipients.filter((r) => String(r.userId || '').trim());
    if (list.length === 0) throw new Error('No recipients');

    const toUserNames = Object.fromEntries(list.map((r) => [r.userId, r.userName]));
    try {
      const created = await this.sendMessageOnce({
        ...message,
        groupTitle: title,
        toUserIds: list.map((r) => r.userId),
        toUserNames,
        memberRoles: message.memberRoles,
        memberPhotos: message.memberPhotos,
        toUserId: GROUP_MESSAGE_TO_USER_ID,
        toUserName: title,
      });
      if (created.id) return created;
    } catch (error) {
      console.error('Group message failed, not sending duplicates:', error);
      throw error;
    }
    throw new Error('Failed to send group message');
  }

  // שליחת הודעה חדשה
  async sendMessage(message: SendMessageInput): Promise<string> {
    const toUserIds = (message.toUserIds || [])
      .map((id) => String(id || '').trim())
      .filter((id) => id && id !== String(message.fromUserId || '').trim());
    const groupTitle = String(message.groupTitle || '').trim();
    if (toUserIds.length > 1 && !message.conversationId && !groupTitle) {
      const recipients = toUserIds.map((id) => ({
        userId: id,
        userName: message.toUserNames?.[id] || message.toUserName || 'משתמש',
      }));
      return this.sendToRecipients(message, recipients);
    }
    const sent = await this.sendMessageOnce(message);
    return sent.id;
  }

  private async sendMessageOnce(
    message: SendMessageInput,
  ): Promise<{ id: string; conversationId?: string; memberIds?: string[] }> {
    try {
      const attachmentFile = message.attachmentFile || null;
      let attachment: any = null;
      if (attachmentFile) {
        const dataBase64 = await this.fileToBase64(attachmentFile);
        attachment = {
          fileName: attachmentFile.name,
          contentType: attachmentFile.type || 'application/octet-stream',
          size: attachmentFile.size,
          dataBase64,
        };
      }
      const payload: any = {
        ...message,
        attachment,
      };
      delete payload.attachmentFile;
      const isGroupPayload =
        Boolean(String(payload.groupTitle || '').trim()) ||
        String(payload.toUserId || '') === GROUP_MESSAGE_TO_USER_ID ||
        String(payload.conversationType || '') === 'group';
      if (!isGroupPayload) {
        delete payload.toUserIds;
        delete payload.toUserNames;
        delete payload.memberRoles;
        delete payload.memberPhotos;
        delete payload.groupTitle;
      }
      const response = await messagingPostWithAuth<MessagesPostResponse>('/api/messages', payload);
      const data = response.data;
      if (data?.broadcast && Array.isArray(data.messages) && data.messages.length > 0) {
        const last = data.messages[data.messages.length - 1];
        return {
          id: String(last?.id || ''),
          conversationId: String(last?.conversationId || '').trim() || undefined,
        };
      }
      return {
        id: String(data?.id || ''),
        conversationId: String(data?.conversationId || '').trim() || undefined,
        memberIds: Array.isArray(data?.memberIds) ? data.memberIds.map(String) : undefined,
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async getUserConversations(userId: string): Promise<MessageConversationMeta[]> {
    const uid = String(userId || '').trim();
    if (!uid) return [];
    try {
      const { data } = await messagingGetWithAuth('/api/messages/conversations', {
        params: { userId: uid },
        timeout: 45000,
      });
      return Array.isArray(data) ? (data as MessageConversationMeta[]) : [];
    } catch {
      return [];
    }
  }

  invalidateUserMessagesCache(userId: string): void {
    const key = String(userId || '').trim();
    if (!key) return;
    this.pendingUserMessages.delete(key);
    this.messagesCacheByUserId.delete(key);
  }

  // קבלת כל ההודעות של משתמש (שקיבל ושלח)
  async getUserMessages(userId: string): Promise<Message[]> {
    const key = String(userId || '').trim();
    if (!key || !this.messagingAllowed()) return [];
    const inFlight = this.pendingUserMessages.get(key);
    if (inFlight) return inFlight;
    const p = (async () => {
      try {
        const { data } = await messagingGetWithAuth('/api/messages', {
          params: { userId: key },
          timeout: 45000,
        });
        const list = Array.isArray(data) ? (data as Message[]) : [];
        this.messagesCacheByUserId.set(key, list);
        return list;
      } catch (error) {
        const status = Number((error as { response?: { status?: number } })?.response?.status || 0);
        if (status !== 403) {
          console.error('Error getting user messages:', error);
        }
        throw error;
      } finally {
        this.pendingUserMessages.delete(key);
      }
    })();
    this.pendingUserMessages.set(key, p);
    return p;
  }

  // קבלת הודעות שלא נקראו
  async getUnreadMessages(userId: string): Promise<Message[]> {
    try {
      const all = await this.getUserMessages(userId);
      return all.filter((msg) => msg.toUserId === userId && !msg.isRead);
    } catch (error) {
      console.error('Error getting unread messages:', error);
      throw error;
    }
  }

  // קבלת מספר הודעות שלא נקראו
  async getUnreadCount(userId: string): Promise<number> {
    const key = String(userId || '').trim();
    if (!key || !this.messagingAllowed()) return 0;
    const inFlight = this.pendingUnreadCount.get(key);
    if (inFlight) return inFlight;
    const p = (async () => {
      try {
        const { data } = await hybridApi.get('/api/messages/unread-count', { params: { userId: key } });
        return Number(data?.count || 0);
      } catch (error) {
        const status = Number((error as { response?: { status?: number } })?.response?.status || 0);
        if (status !== 403) {
          console.error('Error getting unread count:', error);
        }
        return 0;
      } finally {
        this.pendingUnreadCount.delete(key);
      }
    })();
    this.pendingUnreadCount.set(key, p);
    return p;
  }

  async markConversationAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      await hybridApi.patch(`/api/messages/conversations/${conversationId}/read`, { userId });
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (status === 404) return;
      console.error('Error marking conversation as read:', error);
      throw error;
    }
  }

  async createGroupConversation(input: {
    fromUserId: string;
    fromUserName: string;
    fromUserRole: string;
    fromUserPhotoURL?: string;
    title: string;
    members: { userId: string; userName: string; userRole?: string; userPhotoURL?: string }[];
  }): Promise<MessageConversationMeta> {
    try {
      const { data } = await hybridApi.post('/api/messages/conversations', {
        type: 'group',
        ...input,
      });
      return data as MessageConversationMeta;
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (status === 404) {
        return {
          id: '',
          type: 'group',
          title: input.title,
          updatedAt: new Date().toISOString(),
          members: input.members.map((m) => ({
            userId: m.userId,
            userName: m.userName,
            userRole: m.userRole,
            userPhotoURL: m.userPhotoURL,
          })),
        };
      }
      throw error;
    }
  }

  // סימון הודעה כנקראה
  async markAsRead(messageId: string): Promise<void> {
    try {
      await hybridApi.patch(`/api/messages/${messageId}/read`, {});
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  // סימון כל ההודעות כנקראו
  async markAllAsRead(userId: string): Promise<void> {
    try {
      await hybridApi.patch('/api/messages/read-all', { userId });
    } catch (error) {
      console.error('Error marking all messages as read:', error);
      throw error;
    }
  }

  // מחיקת הודעה
  async deleteMessage(messageId: string): Promise<void> {
    try {
      await hybridApi.delete(`/api/messages/${messageId}`);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  async editMessage(messageId: string, content: string): Promise<void> {
    try {
      await hybridApi.patch(`/api/messages/${messageId}`, { content });
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  // קבלת שיחה מסוימת (כל ההודעות בין 2 משתמשים)
  async getConversation(userId1: string, userId2: string): Promise<Message[]> {
    try {
      const all = await this.getUserMessages(userId1);
      return all
        .filter((msg) => {
          const direct = msg.fromUserId === userId1 && msg.toUserId === userId2;
          const inverse = msg.fromUserId === userId2 && msg.toUserId === userId1;
          return direct || inverse;
        })
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    } catch (error) {
      console.error('Error getting conversation:', error);
      throw error;
    }
  }

  // האזנה לשינויים בהודעות בזמן אמת (שקיבלתי ושלחתי)
  subscribeToMessages(userId: string, callback: MessageCallback): Unsubscribe {
    const uid = String(userId || '').trim();
    if (!uid || !this.messagingAllowed()) return () => {};
    const key = `messages-${uid}-${Math.random().toString(36).slice(2)}`;
    if (!this.messageSubscribers.has(uid)) {
      this.messageSubscribers.set(uid, new Set());
    }
    this.messageSubscribers.get(uid)?.add(callback);
    // הצגה מיידית מהמטמון (פתיחה חוזרת של הדיאלוג) — לא ממתינים ל-roundtrip לשרת.
    const cachedMessages = this.messagesCacheByUserId.get(uid);
    if (cachedMessages && cachedMessages.length > 0) {
      callback(cachedMessages);
    }
    void this.refreshMessagesForUser(uid);
    void this.ensureSocketConnected(uid);
    const unsubscribe = () => {
      const callbacks = this.messageSubscribers.get(uid);
      callbacks?.delete(callback);
      if (callbacks && callbacks.size === 0) this.messageSubscribers.delete(uid);
    };
    this.unsubscribeCallbacks.set(key, unsubscribe);
    return unsubscribe;
  }

  // האזנה למספר הודעות שלא נקראו בזמן אמת
  subscribeToUnreadCount(userId: string, callback: UnreadCallback): Unsubscribe {
    const uid = String(userId || '').trim();
    if (!uid || !this.messagingAllowed()) return () => {};
    const key = `unread-${uid}-${Math.random().toString(36).slice(2)}`;
    if (!this.unreadSubscribers.has(uid)) {
      this.unreadSubscribers.set(uid, new Set());
    }
    this.unreadSubscribers.get(uid)?.add(callback);
    const cachedCount = this.unreadCountCacheByUserId.get(uid);
    if (typeof cachedCount === 'number') callback(cachedCount);
    void this.refreshUnreadForUser(uid);
    void this.ensureSocketConnected(uid);
    const unsubscribe = () => {
      const callbacks = this.unreadSubscribers.get(uid);
      callbacks?.delete(callback);
      if (callbacks && callbacks.size === 0) this.unreadSubscribers.delete(uid);
    };
    this.unsubscribeCallbacks.set(key, unsubscribe);
    return unsubscribe;
  }

  /** מרענן מיד את מונה הלא-נקרא בכותרת */
  refreshUnreadCountNow(userId: string): void {
    void this.refreshUnreadForUser(String(userId || '').trim());
  }

  /** עדכון מיידי של badge לא-נקרא בצד לקוח (לפני roundtrip לשרת) */
  decreaseUnreadCountNow(userId: string, by = 1): void {
    const uid = String(userId || '').trim();
    if (!uid) return;
    const current = this.unreadCountCacheByUserId.get(uid);
    if (typeof current !== 'number') {
      void this.refreshUnreadForUser(uid);
      return;
    }
    this.setUnreadCount(uid, Math.max(0, current - Math.max(0, Number(by) || 0)));
  }

  private notifyMessageSubscribers(userId: string, messages: Message[]): void {
    const callbacks = this.messageSubscribers.get(userId);
    if (!callbacks) return;
    callbacks.forEach((cb) => cb(messages));
  }

  private notifyUnreadSubscribers(userId: string, count: number): void {
    const callbacks = this.unreadSubscribers.get(userId);
    if (!callbacks) return;
    callbacks.forEach((cb) => cb(count));
  }

  private async refreshMessagesForUser(userId: string): Promise<void> {
    if (!userId || !this.messagingAllowed()) return;
    try {
      const messages = await this.getUserMessages(userId);
      this.messagesCacheByUserId.set(userId, messages);
      this.notifyMessageSubscribers(userId, messages);
    } catch (error) {
      const status = Number((error as { response?: { status?: number } })?.response?.status || 0);
      if (status !== 403) {
        console.error('Error fetching combined messages:', error);
      }
      this.notifyMessageSubscribers(userId, []);
    }
  }

  private async refreshUnreadForUser(userId: string): Promise<void> {
    if (!userId || !this.messagingAllowed()) return;
    try {
      const count = await this.getUnreadCount(userId);
      this.setUnreadCount(userId, count);
    } catch {
      // silent; caller UI already keeps last stable value
    }
  }

  private setUnreadCount(userId: string, count: number): void {
    const safe = Math.max(0, Number(count || 0));
    this.unreadCountCacheByUserId.set(userId, safe);
    this.notifyUnreadSubscribers(userId, safe);
  }

  /** תיקון דגלים מה-socket (השרת הישן שלח isSent/isRead לפי השולח בלבד) */
  private normalizeMessageForViewer(message: Message, viewerId: string): Message {
    const viewer = String(viewerId || '').trim();
    const fromId = String(message.fromUserId || '').trim();
    const isSent = fromId === viewer;
    const isGroup =
      message.conversationType !== 'direct' &&
      (message.conversationType === 'group' ||
        String(message.toUserId || '') === GROUP_MESSAGE_TO_USER_ID);
    if (!isGroup) return { ...message, isSent };
    const serverMarkedSent = Boolean(message.isSent);
    if (serverMarkedSent && !isSent) {
      return { ...message, isSent: false, isRead: false };
    }
    return { ...message, isSent, isRead: isSent ? true : message.isRead };
  }

  private mergeIncomingMessage(userId: string, incoming: Message): void {
    const current = this.messagesCacheByUserId.get(userId) || [];
    const idx = current.findIndex((m) => String(m.id || '') === String(incoming.id || ''));
    const next =
      idx >= 0
        ? current.map((m, i) => (i === idx ? { ...m, ...incoming } : m))
        : [incoming, ...current];
    next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    this.messagesCacheByUserId.set(userId, next);
    this.notifyMessageSubscribers(userId, next);
  }

  private scheduleRefresh(userId: string): void {
    const uid = String(userId || '').trim();
    if (!uid || !this.messagingAllowed()) return;
    const timer = this.pendingMessageRefreshTimers.get(uid);
    if (timer) clearTimeout(timer);
    const next = setTimeout(() => {
      this.pendingMessageRefreshTimers.delete(uid);
      void this.refreshMessagesForUser(uid);
      void this.refreshUnreadForUser(uid);
    }, 120);
    this.pendingMessageRefreshTimers.set(uid, next);
  }

  private activeUserId(): string {
    try {
      return String(getAuth().currentUser?.uid || '').trim();
    } catch {
      return '';
    }
  }

  private async ensureSocketConnected(userId: string): Promise<void> {
    const uid = String(userId || '').trim();
    if (!uid || !this.messagingAllowed()) return;
    this.registerSocketListeners();
    await realtimeSocketHub.ensureConnected(uid);
  }

  /** מצמיד את ה-listeners של ההודעות ל-socket המשותף (פעם אחת; מתחבר מחדש אוטומטית). */
  private registerSocketListeners(): void {
    if (this.socketListenersRegistered) return;
    this.socketListenersRegistered = true;
    this.detachConnectListener = realtimeSocketHub.onConnect(() => {
      const uid = this.activeUserId();
      if (uid && this.messagingAllowed()) this.scheduleRefresh(uid);
    });
    // הטאב חוזר לפוקוס / המכשיר חוזר לאונליין — ייתכן שהודעות הגיעו בזמן שה-socket היה מנותק
    // בשקט (שינה/רקע ממושך) ואף אירוע messages:new לא התקבל. רענון יזום תופס אותן מיד.
    this.detachWakeListener = realtimeSocketHub.onWake(() => {
      const uid = this.activeUserId();
      if (uid && this.messagingAllowed()) this.scheduleRefresh(uid);
    });
    this.detachSocketConsumer = realtimeSocketHub.onSocket((socket) => {
      socket.on('messages:updated', () => {
        const uid = this.activeUserId();
        if (uid) this.scheduleRefresh(uid);
      });
      socket.on('messages:unread-count', (payload: UnreadCountEvent) => {
        const uid = this.activeUserId();
        const eventUid = String(payload?.userId || '').trim();
        if (!uid || !eventUid || uid !== eventUid) return;
        this.setUnreadCount(uid, Number(payload?.count || 0));
      });
      socket.on('messages:new', (payload: MessageEventPayload) => {
        const uid = this.activeUserId();
        const raw = (payload?.message || null) as Message | null;
        if (!uid || !raw?.id) return;
        const message = this.normalizeMessageForViewer(raw, uid);
        this.mergeIncomingMessage(uid, message);
        const isGroup =
          message.conversationType !== 'direct' &&
          (message.conversationType === 'group' ||
            String(message.toUserId || '') === GROUP_MESSAGE_TO_USER_ID);
        const isIncoming = isGroup
          ? String(message.fromUserId || '') !== uid && !message.isRead
          : !message.isSent && !message.isRead;
        if (isIncoming) {
          notifyIncomingMessage(message, uid);
          const current = this.unreadCountCacheByUserId.get(uid);
          if (typeof current === 'number') {
            this.setUnreadCount(uid, current + 1);
          } else {
            void this.refreshUnreadForUser(uid);
          }
        }
      });
    });
  }

  // ביטול כל ההאזנות
  unsubscribeAll(): void {
    this.unsubscribeCallbacks.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeCallbacks.clear();
    this.pendingMessageRefreshTimers.forEach((timer) => clearTimeout(timer));
    this.pendingMessageRefreshTimers.clear();
    this.messageSubscribers.clear();
    this.unreadSubscribers.clear();
    this.messagesCacheByUserId.clear();
    this.unreadCountCacheByUserId.clear();
    this.pendingUserMessages.clear();
    this.pendingUnreadCount.clear();
    // החיבור עצמו בבעלות realtimeSocketHub (משותף עם AppRealtimeService) — ננתק רק את
    // ה-listeners של ההודעות; ניתוק ה-socket מתבצע ב-appRealtimeService.disconnect() בעת logout.
    this.detachSocketConsumer?.();
    this.detachSocketConsumer = null;
    this.detachConnectListener?.();
    this.detachConnectListener = null;
    this.detachWakeListener?.();
    this.detachWakeListener = null;
    this.socketListenersRegistered = false;
  }

  private async fileToBase64(file: File): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read attachment file'));
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.readAsDataURL(file);
    });
  }
}

export const messagingService = new MessagingService();

